import React, { useState, useEffect } from 'react';
import DataTable from 'react-data-table-component';
import { authorizedRequest } from '../../api/authorizedRequest';

interface EmployeeCheckDto {
  date: string;
  endDate: string;
  sequence: string;
  checkNumber: string;
  grossPay: number;
  netPay: number;
  employeeId: string;
}

interface PagedResultDto<T> {
  totalCount: number;
  items: T[];
}

const EmployeeChecks: React.FC = () => {
  const [checks, setChecks] = useState<PagedResultDto<EmployeeCheckDto>>({ totalCount: 0, items: [] });
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterCheckDate, setFilterCheckDate] = useState<string>('');
  const [filterSequence, setFilterSequence] = useState<string>('');
  const [filterCheckNumber, setFilterCheckNumber] = useState<string>('');

  // Local state for filter inputs (to prevent ontype firing)
  const [inputCheckDate, setInputCheckDate] = useState<string>('');
  const [inputSequence, setInputSequence] = useState<string>('');
  const [inputCheckNumber, setInputCheckNumber] = useState<string>('');

  const [sortColumn, setSortColumn] = useState<string>('');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const fetchChecks = async () => {
    try {
      const params = {
        CheckDate: filterCheckDate || undefined,
        Sequence: filterSequence || undefined,
        CheckNumber: filterCheckNumber || undefined,
        MaxResultCount: rowsPerPage,
        SkipCount: (currentPage - 1) * rowsPerPage,
        Sorting: sortColumn ? `${sortColumn} ${sortDirection}` : undefined,
      };
      const response = await authorizedRequest({ url: '/Employee/Checks', method: 'GET', params });
      setChecks(response.data);
    } catch (error) {
      console.error('Error fetching employee checks:', error);
    }
  };

  // Fetch checks on mount and when pagination/filter changes
  useEffect(() => {
    fetchChecks();
    // eslint-disable-next-line
  }, [filterCheckDate, filterSequence, filterCheckNumber, currentPage, rowsPerPage, sortColumn, sortDirection]);

  // Accordion state
  const [filterOpen, setFilterOpen] = useState(false);

  const handleApplyFilter = (e: React.FormEvent) => {
    e.preventDefault();
    setFilterCheckDate(inputCheckDate);
    setFilterSequence(inputSequence);
    setFilterCheckNumber(inputCheckNumber);
    setCurrentPage(1); // Reset to first page on filter
  };

  const handleClearFilter = () => {
    setInputCheckDate('');
    setInputSequence('');
    setInputCheckNumber('');
    setFilterCheckDate('');
    setFilterSequence('');
    setFilterCheckNumber('');
    setCurrentPage(1);
  };

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#343a40', // dark background
        color: 'white',
      },
    },
    headCells: {
      style: {
        color: 'white', // light foreground for text and sort icons
        fontWeight: 'bold',
      },
    },
  };

  const columns = [
    {
      name: 'Check Date',
      selector: (row: EmployeeCheckDto) =>
  new Date(row.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }),
      sortable: true,
      sortField: 'date',
       width: "25%",
    },
    {
      name: 'Check Number',
      selector: (row: EmployeeCheckDto) => row.checkNumber,
      sortable: true,
      sortField: 'chequeNumber',
       width: "20%",
    },
    {
      name: 'Gross Pay',
      selector: (row: EmployeeCheckDto) => row.grossPay.toFixed(2),
      sortable: true,
      sortField: 'grossPay',
       width: "20%",
    },
    {
      name: 'Net Pay',
      selector: (row: EmployeeCheckDto) => row.netPay.toFixed(2),
      sortable: true,
      sortField: 'netPay',
       width: "20%",
    },
    {
      name: 'Actions',
      cell: (row: EmployeeCheckDto) => (
        <button
          className="btn btn-secondary btn-sm"
          onClick={() =>
            window.location.href = `/employee-check-details?checkdate=${row.date.toString().substring(0, 10)}&enddate=${row.endDate.toString().substring(0, 10)}&sequence=${row.sequence}`
          }
        >
          View
        </button>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
       width: "15%",
    },
  ];

  return (
      <div className="pd-ltr-20">
        <div className="card-box pd-20 height-100-p mb-30">
          <div className="row align-items-center">
            <div className="col-md-12">
              <h4 className="font-20 weight-500 mb-10 text-capitalize">
                Employee Checks
              </h4>
            </div>
          </div>
        </div>
                <div className="accordion mb-3" id="filterAccordion">
          <div className="card">
            <div className="card-header p-0" id="headingFilter">
              <h2 className="mb-0">
                <button
                  className="btn btn-link btn-block text-left"
                  type="button"
                  onClick={() => setFilterOpen(!filterOpen)}
                  aria-expanded={filterOpen}
                  aria-controls="collapseFilter"
                >
                  Filter
                </button>
              </h2>
            </div>
            <div
              id="collapseFilter"
              className={`collapse${filterOpen ? ' show' : ''}`}
              aria-labelledby="headingFilter"
              data-parent="#filterAccordion"
            >
              <div className="card-body">
                <form onSubmit={handleApplyFilter} autoComplete="off">
                  <div className="row">
                    <div className="col-md-4 mb-2">
                      <input
                        type="date"
                        className="form-control"
                        placeholder="Filter by Check Date"
                        value={inputCheckDate}
                        onChange={e => setInputCheckDate(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Filter by Check Number"
                        value={inputCheckNumber}
                        onChange={e => setInputCheckNumber(e.target.value)}
                      />
                    </div>
                    <div className="col-md-4 mb-2">
                      <div className="col-12 text-right">
                        <button type="submit" className="btn btn-secondary mt-2 mr-2">
                          Apply Filter
                        </button>
                        <button type="button" className="btn btn-outline-secondary mt-2" onClick={handleClearFilter}>
                          Clear Filter
                        </button>
                      </div>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
        <div className="card-box mb-30">
          <div className='min-height-200px'>
            <div className="pd-20">
        <DataTable
          columns={columns}
          customStyles={customStyles}
          data={checks.items}
          pagination
          paginationServer
          paginationTotalRows={checks.totalCount}
          paginationPerPage={rowsPerPage}
          paginationDefaultPage={currentPage}
          onChangeRowsPerPage={(newPerPage) => setRowsPerPage(newPerPage)}
          onChangePage={(page) => setCurrentPage(page)}
          sortServer
          highlightOnHover
          pointerOnHover
          onSort={(column, sortDirection) => {
            setSortColumn(column.sortField || '');
            setSortDirection(sortDirection);
          }}
        />
            </div>
          </div>
        </div>
      </div>
  );
};

export default EmployeeChecks;
